import { sharedReviewEnabled, messageStatus, saveMessage, messageAudio, approveMessage } from './personalMessagesApi';
import { emitMeasurementEvent } from '../api/measurementApi';
import { createVoice } from './captureApi';
import {
  fetchCaptureStatus,
  fetchSessionConfig,
  SampleUploadResult,
  SessionConfig,
  uploadTake,
  uploadVerbatim,
} from './sessionApi';
import {
  CaptureClient,
  CaptureProgress,
  CaptureQualityReport,
  CaptureSessionConfig,
  CaptureTarget,
  CaptureUploadResult,
  ListenerCaptureTarget,
  VoiceCaptureTarget,
} from './captureClient';

interface AuthenticatedCaptureCallbacks {
  draftScopeKey: string;
  startVoiceBuild: (voiceId: string) => Promise<boolean>;
  refreshVoices: () => Promise<void>;
  listenerContext?: {
    target: ListenerCaptureTarget;
    displayLabel: string;
  };
}

function requireVoiceId(target: CaptureTarget): string {
  if (!target.voiceId) throw new Error('Capture target is unavailable.');
  return target.voiceId;
}

function requireListenerTarget(
  target: CaptureTarget,
): ListenerCaptureTarget & { voiceId: string } {
  if (target.kind !== 'listener' || !target.voiceId || !target.listenerId) {
    throw new Error('Listener capture target is unavailable.');
  }
  return target as ListenerCaptureTarget & { voiceId: string };
}

function normalizeConfig(config: SessionConfig | null): CaptureSessionConfig | null {
  if (!config) return null;
  return {
    captureVersion: config.capture_version,
    qaVersion: config.qa_version,
    micCheck: {
      roomToneS: config.mic_check.room_tone_s,
      speechS: config.mic_check.speech_s,
      sentence: config.mic_check.sentence,
      snrPassDb: config.mic_check.snr_pass_db,
      snrWarnDb: config.mic_check.snr_warn_db,
      peakMaxDb: config.mic_check.peak_max_db,
    },
    takePrompts: config.take_prompts.map((prompt) => ({
      style: prompt.style,
      title: prompt.title,
      coaching: prompt.coaching,
      text: prompt.text,
      minS: prompt.min_s,
      maxS: prompt.max_s,
    })),
    verbatimPrompts: config.verbatim_prompts.map((prompt) => ({
      key: prompt.key,
      title: prompt.title,
      template: prompt.template,
      coaching: prompt.coaching,
    })),
  };
}

function normalizeQuality(result: SampleUploadResult): CaptureQualityReport {
  return {
    durationSeconds: result.quality_report.duration_seconds,
    loudnessDb: result.quality_report.loudness_db,
    signalQuality: result.quality_report.signal_quality,
    clipping: result.quality_report.clipping,
    clippingPercent: result.quality_report.clipping_percent,
    silencePercent: result.quality_report.silence_percent,
    snrDb: result.quality_report.snr_db,
    sampleRate: result.quality_report.sample_rate,
  };
}

function normalizeUpload(result: SampleUploadResult): CaptureUploadResult {
  return {
    accepted: result.accepted,
    storageConfirmed: result.storage_confirmed,
    qualityReport: normalizeQuality(result),
    rejectionReasons: result.rejection_reasons,
    suggestions: result.suggestions,
  };
}

function normalizeProgress(
  target: CaptureTarget,
  status: Awaited<ReturnType<typeof fetchCaptureStatus>>,
): CaptureProgress {
  const voiceId = requireVoiceId(target);
  if (status.voice_id !== voiceId) {
    throw new Error('Capture progress did not match the requested target.');
  }
  if (target.kind === 'listener' && status.listener_id !== target.listenerId) {
    throw new Error('Capture progress did not match the requested listener.');
  }
  if (target.kind === 'voice' && status.listener_id !== null) {
    throw new Error('Capture progress did not match voice capture.');
  }
  return {
    target,
    complete: status.complete,
    completedTakeIndexes: status.persisted_sample_indexes
      .filter((index) => Number.isInteger(index) && index >= 1 && index <= 6)
      .map((index) => index - 1),
    completedClipKeys: status.persisted_verbatim_keys.filter(
      (key): key is 'intro' | 'name' | 'goodnight' => (
        key === 'intro' || key === 'name' || key === 'goodnight'
      ),
    ),
    missing: [...status.missing],
  };
}

export function createAuthenticatedCaptureClient(
  callbacks: AuthenticatedCaptureCallbacks,
): CaptureClient {
  if (!callbacks.draftScopeKey.trim()) {
    throw new Error('Capture draft scope is unavailable.');
  }
  const requireAuthorizedListener = (target: CaptureTarget) => {
    const listenerTarget = requireListenerTarget(target);
    const context = callbacks.listenerContext;
    if (
      !context
      || context.target.voiceId !== listenerTarget.voiceId
      || context.target.listenerId !== listenerTarget.listenerId
      || !context.displayLabel.trim()
    ) {
      throw new Error('Listener capture target is not authorized by this client.');
    }
    return { target: listenerTarget, displayLabel: context.displayLabel };
  };
  const uploadIds = new WeakMap<Blob, string>();
  return {
    requiresComponentReview: sharedReviewEnabled,
    loadReviewAudio: (target, receipt) => messageAudio(requireAuthorizedListener(target).target, receipt),
    approveRecording: (target, receipt, requestId) => approveMessage(requireAuthorizedListener(target).target, receipt, requestId),
    emitDiagnostic: (event) => emitMeasurementEvent({ ...event, properties: { ...event.properties } }),
    draftScopeKey: callbacks.draftScopeKey,
    voiceTargetRequiresId: true,
    async fetchSessionConfig() {
      return normalizeConfig(await fetchSessionConfig());
    },
    async ensureVoice(label: string): Promise<VoiceCaptureTarget> {
      const created = await createVoice(label);
      if (!created.voice_id) throw new Error('Voice creation did not return a target.');
      return { kind: 'voice', voiceId: created.voice_id };
    },
    async fetchProgress(target: CaptureTarget) {
      if (sharedReviewEnabled && target.kind === 'listener') {
        const messages = await messageStatus(requireAuthorizedListener(target).target);
        const values = [messages.intro, messages.goodnight];
        return {target, completedTakeIndexes: [],
          completedClipKeys: values.filter(item => item?.status === 'approved').map(item => item!.kind),
          pendingReviews: values.filter(item => item?.status === 'pending') as NonNullable<typeof values[number]>[],
          complete: values.every(item => item?.status === 'approved'),
          missing: ['intro', 'goodnight'].filter((_, index) => values[index]?.status !== 'approved'),
        };
      }
      const voiceId = requireVoiceId(target);
      const status = target.kind === 'listener'
        ? await (() => {
          const listener = requireAuthorizedListener(target);
          return fetchCaptureStatus(
            voiceId,
            listener.displayLabel,
            listener.target.listenerId,
          );
        })()
        : await fetchCaptureStatus(voiceId);
      const progress = normalizeProgress(target, status);
      return progress;
    },
    async uploadTake(target, take, blob) {
      if (target.kind !== 'voice') {
        throw new Error('Voice take cannot use a listener target.');
      }
      const result = await uploadTake({
        voiceId: requireVoiceId(target),
        sampleIndex: take.sampleIndex,
        style: take.style,
        wavBlob: blob,
        stats: take.stats,
        voiceName: take.voiceLabel,
        forced: take.forced,
      });
      return normalizeUpload(result);
    },
    async uploadListenerClip(target, clip, blob) {
      const listener = requireAuthorizedListener(target);
      if (sharedReviewEnabled && clip.clipKey !== 'name') {
        let requestId = uploadIds.get(blob);
        if (!requestId) { requestId = crypto.randomUUID(); uploadIds.set(blob, requestId); }
        const receipt = await saveMessage(listener.target, clip.clipKey, blob, requestId);
        if ('accepted' in receipt) {
          return {accepted: false, storageConfirmed: false,
            qualityReport: {durationSeconds: 0, loudnessDb: 0},
            rejectionReasons: receipt.rejection_reasons};
        }
        return { accepted: true, storageConfirmed: true, componentReview: receipt,
          qualityReport: {durationSeconds: 0, loudnessDb: 0} };
      }
      const result = await uploadVerbatim({
        voiceId: listener.target.voiceId,
        listenerId: listener.target.listenerId,
        childName: listener.displayLabel,
        clipKey: clip.clipKey,
        wavBlob: blob,
        forced: clip.forced,
      });
      return normalizeUpload(result);
    },
    async complete(target) {
      if (target.kind === 'voice') {
        const started = await callbacks.startVoiceBuild(requireVoiceId(target));
        if (!started) throw new Error('Voice build could not start.');
        return;
      }
      const listener = requireAuthorizedListener(target);
      if (sharedReviewEnabled) {
        const messages = await messageStatus(listener.target);
        if (messages.intro?.status !== 'approved' || messages.goodnight?.status !== 'approved') throw new Error('Review both saved messages before continuing.');
      }
      await callbacks.refreshVoices();
    },
  };
}

export type { AuthenticatedCaptureCallbacks };
